/**
 * Offre "2 ebooks individuels éligibles achetés = ChatGPT facile offert".
 *
 * ARCHITECTURE v4 — machine à états PURE, sans aucune mémoire fragile.
 *
 * Règle commerciale, sans exception ni mémoire d'historique :
 *   - panier réel avec >= threshold ebooks éligibles => ChatGPT facile présent
 *     UNE fois, quantité 1, marqué comme cadeau (propriété _free_gift).
 *   - panier réel avec < threshold ebooks éligibles => ChatGPT facile absent.
 * Rien d'autre n'entre en jeu : ni un historique de retrait volontaire, ni un
 * compteur précédent, ni sessionStorage/localStorage. La seule source de
 * vérité est une lecture fraîche du panier Shopify au moment de la décision.
 * Les trois bugs précédents (v1, v2, v3) venaient tous, directement ou
 * indirectement, d'une tentative de mémoriser un état passé (dismissedForCount,
 * sessionStorage, puis signature de composition + "retrait volontaire") :
 * cette version élimine complètement cette catégorie de bug en ne se basant
 * plus jamais que sur l'état présent réel du panier.
 *
 * Concurrence : une file d'exécution unique (`busy`). Un événement panier
 * reçu pendant qu'un cycle tourne ne déclenche pas un second cycle parallèle :
 * il pose `dirty = true`, et un nouveau cycle complet (nouvelle lecture
 * fraîche du panier) est rejoué dès que le cycle en cours se termine — aucun
 * événement n'est donc jamais perdu, et aucune décision n'est jamais prise
 * sur un état supposé. Après toute mutation, le panier est relu, l'UI est
 * rafraîchie, puis le cycle est immédiatement rejoué pour confirmer que
 * l'état réel obtenu correspond bien à la règle (utile par ex. si plusieurs
 * corrections successives sont nécessaires) ; il s'arrête de lui-même dès que
 * la décision devient "rien à faire".
 *
 * Chaque mutation est traitée comme une seule ligne de panier identifiée par
 * variant_id == giftVariantId, indépendamment de la présence ou non de la
 * propriété _free_gift — donc un ajout manuel de ChatGPT facile (URL directe,
 * appel API, lien produit) est traité exactement comme n'importe quelle autre
 * présence : retiré si le panier n'est pas/plus éligible, normalisé (quantité
 * 1 + propriété cadeau) sinon.
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[chatgpt-gift]';
  var GIFT_PROPERTY = '_free_gift';

  function log() {
    if (window.console && console.log) {
      console.log.apply(console, [LOG_PREFIX].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function logError() {
    if (window.console && console.error) {
      console.error.apply(console, [LOG_PREFIX].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    try {
      init();
    } catch (e) {
      logError('erreur à l\'initialisation, le script est désactivé pour cette page', e);
    }
  });

  function init() {
    var config = window.chatgptGiftOfferConfig;
    if (!config) {
      logError('window.chatgptGiftOfferConfig est introuvable — le script ne peut pas démarrer.');
      return;
    }
    if (!Array.isArray(config.eligibleVariantIds) || config.eligibleVariantIds.length === 0) {
      logError(
        'la liste des variantes éligibles est vide — vérifiez que la collection ' +
          '"ebooks-eligibles-offre-chatgpt-facile" est publiée et contient des produits.',
        config
      );
      return;
    }
    if (!config.giftVariantId) {
      logError('giftVariantId manquant dans la configuration.', config);
      return;
    }
    if (typeof subscribe !== 'function' || typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      logError('PUB_SUB_EVENTS/subscribe/publish indisponibles — constants.js ou pubsub.js n\'est pas chargé.');
      return;
    }
    if (!window.routes || !window.routes.cart_url || !window.routes.cart_add_url || !window.routes.cart_change_url) {
      logError('window.routes incomplet (cart_url / cart_add_url / cart_change_url requis).', window.routes);
      return;
    }

    var eligibleIds = {};
    config.eligibleVariantIds.forEach(function (id) {
      eligibleIds[String(id)] = true;
    });
    var giftVariantId = String(config.giftVariantId);
    var threshold = config.threshold || 2;

    log(
      'initialisé.', Object.keys(eligibleIds).length, 'variante(s) éligible(s), cadeau =', giftVariantId,
      ', seuil =', threshold
    );

    // File d'exécution unique : aucun cycle ne démarre tant qu'un autre est en
    // cours. `dirty` garantit qu'un événement survenu pendant un cycle en
    // cours redéclenche un cycle complet et frais juste après, sans être perdu.
    var busy = false;
    var dirty = false;

    function countEligibleQuantity(cart) {
      // Décision explicite sur la gestion des quantités : la quantité de
      // chaque ligne éligible est additionnée (acheter 2 exemplaires du même
      // ebook compte pour 2 unités éligibles, exactement comme 2 ebooks
      // différents en quantité 1 chacun). C'est le comportement déjà en place
      // avant cette refonte ; il est conservé tel quel car rien dans la
      // demande commerciale ne distingue "articles distincts" de "quantité
      // cumulée", et additionner les quantités correspond au sens le plus
      // naturel de "2 ebooks éligibles achetés".
      var count = 0;
      cart.items.forEach(function (item) {
        if (eligibleIds[String(item.variant_id)]) {
          count += item.quantity;
        }
      });
      return count;
    }

    function findGiftLines(cart) {
      // Toute ligne portant la variante ChatGPT facile est concernée, qu'elle
      // ait été ajoutée par ce script (propriété _free_gift) ou manuellement
      // par le client (URL produit, appel direct à /cart/add.js, etc.).
      return cart.items.filter(function (item) {
        return String(item.variant_id) === giftVariantId;
      });
    }

    function isNormalized(line) {
      return line.quantity === 1 && !!(line.properties && line.properties[GIFT_PROPERTY] === '1');
    }

    // Détermine, à partir de l'état RÉEL du panier, l'action à effectuer.
    // Ne retourne jamais plus d'une action : si plusieurs corrections sont
    // nécessaires (ex. deux lignes ChatGPT présentes en même temps), une
    // seule est traitée par cycle, le cycle étant rejoué automatiquement
    // ensuite jusqu'à ce que "aucune action" soit atteint.
    function decide(cart) {
      var eligibleQuantity = countEligibleQuantity(cart);
      var giftLines = findGiftLines(cart);

      if (eligibleQuantity >= threshold) {
        if (giftLines.length === 0) {
          return { type: 'add' };
        }
        if (giftLines.length > 1) {
          return { type: 'remove_line', key: giftLines[1].key, reason: 'ligne cadeau en double' };
        }
        if (!isNormalized(giftLines[0])) {
          return { type: 'normalize', key: giftLines[0].key };
        }
        return { type: 'none' };
      }

      if (giftLines.length > 0) {
        return { type: 'remove_line', key: giftLines[0].key, reason: 'sous le seuil d\'éligibilité' };
      }
      return { type: 'none' };
    }

    function fetchFreshCart() {
      return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
        return r.json();
      });
    }

    function addGift() {
      var properties = {};
      properties[GIFT_PROPERTY] = '1';
      return fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: giftVariantId, quantity: 1, properties: properties }] }),
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok || data.status) {
            throw new Error('/cart/add.js a refusé l\'ajout du cadeau : ' + JSON.stringify(data));
          }
          return data;
        });
      });
    }

    function changeLine(key, quantity, properties) {
      var body = { id: key, quantity: quantity };
      if (properties) body.properties = properties;
      return fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok || data.status) {
            throw new Error('/cart/change.js a échoué : ' + JSON.stringify(data));
          }
          return data;
        });
      });
    }

    function performAction(action) {
      log('mutation démarrée —', action.type, action.key || '', action.reason || '');
      if (action.type === 'add') {
        return addGift();
      }
      if (action.type === 'normalize') {
        var properties = {};
        properties[GIFT_PROPERTY] = '1';
        return changeLine(action.key, 1, properties);
      }
      // 'remove_line'
      return changeLine(action.key, 0);
    }

    function notifyCartUpdated(cartData) {
      // Rafraîchit le panier classique, le tiroir et la bulle d'icône panier
      // (mécanisme déjà utilisé par le reste du thème). `source` distinct de
      // 'gift-with-purchase' pour que nos propres abonnés ne se re-déclenchent
      // pas en boucle sur notre propre notification.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-with-purchase-sync', cartData: cartData });
    }

    // Un seul cycle "lire -> décider -> agir -> relire -> notifier" à la fois.
    // Si un cycle se termine avec une action effectuée, un nouveau cycle est
    // immédiatement rejoué (re-décision sur l'état frais) pour confirmer la
    // convergence, jusqu'à ce que "none" soit atteint ou qu'une erreur survienne.
    function runCycle() {
      if (busy) {
        log('cycle demandé pendant qu\'un autre est en cours — sera rejoué juste après.');
        dirty = true;
        return;
      }
      busy = true;
      dirty = false;

      fetchFreshCart()
        .then(function (cart) {
          var eligibleQuantity = countEligibleQuantity(cart);
          var giftLines = findGiftLines(cart);
          var action = decide(cart);

          log(
            'évaluation — quantité éligible:', eligibleQuantity,
            '| lignes cadeau:', giftLines.length,
            '| décision:', action.type
          );

          if (action.type === 'none') {
            return null;
          }

          return performAction(action)
            .then(function (result) {
              log('mutation réussie —', action.type, result);
              return fetchFreshCart();
            })
            .then(function (freshCart) {
              log(
                'relecture post-mutation — quantité éligible:', countEligibleQuantity(freshCart),
                '| lignes cadeau:', findGiftLines(freshCart).length
              );
              notifyCartUpdated(freshCart);
              // Une action vient d'être effectuée : on force un nouveau cycle
              // complet pour confirmer la convergence (ex. seconde ligne
              // cadeau à retirer, ou ajout suivi d'une vérification finale).
              dirty = true;
            });
        })
        .catch(function (e) {
          logError('échec du cycle (lecture, décision ou mutation)', e);
        })
        .finally(function () {
          busy = false;
          if (dirty) {
            dirty = false;
            runCycle();
          }
        });
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && (event.source === 'gift-with-purchase-sync')) return;
      try {
        runCycle();
      } catch (e) {
        logError('erreur pendant le traitement d\'un événement panier', e);
      }
    });

    // Évalue l'état réel du panier au chargement de chaque page (arrivée
    // directe sur /cart, rechargement, retour navigateur, panier déjà rempli).
    runCycle();
  }
})();
